import config from "./config";
import URI from "urijs";
import axios from "axios";
import axiosRetry, { isRetryableError } from "axios-retry";
import { logger } from "./logger";
import { getXRoutingDeploymentValue, setAndGetLwaUniqId } from "./cookie-utils";

const preAuthnEndpoint = new URI(config.iamCoreUrl)
  .segment("api/v1/lwa-config/pre-authn")
  .toString();
const postAuthnEndpoint = new URI(config.iamCoreUrl)
  .segment("api/v1/lwa-config/post-authn")
  .toString();
const manageProfileEndpoint = new URI(config.iamCoreUrl)
  .segment("api/v1/lwa-config/manage-profile")
  .toString();
const processCredsEndpoint = new URI(config.iamCoreUrl)
  .segment("api/v1/process-creds")
  .toString();

function generateOptions(data) {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Routing-Deployment": getXRoutingDeploymentValue(),
      "X-Lwa-Uniq-Id": setAndGetLwaUniqId(),
    },
    data,
  };
}

axiosRetry(axios, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    return (
      isRetryableError(error) ||
      error.response?.status === 429 ||
      error?.code === "ECONNABORTED"
    );
  },
  shouldResetTimeout: true,
});

function _getErrorType(error) {
  if (error.response?.status >= 500 || error.status >= 500) {
    return 'ise';
  } else if (error.code === 'ECONNABORTED') {
    return 'timeout';
  } else if (!error.response) {
    return 'network';
  }
  return 'unknown';
}

const fetchWithTimeout = async ({ authnEndpoint, options, onRetry, source }) => {
  // p99.99 of observed pre-authn response times is ~4.5s and the slowest non-timed-out
  // response seen was ~6.9s, so 10s gives headroom without making failures wait 30s+.
  try {
    const response = await axios({
      url: authnEndpoint,
      ...options,
      onRetry,
      timeout: 10000,
    });
    return response.data;
  } catch (err) {
    logger.error('Error occurred while making a call to iam-core', JSON.stringify(err));
    const data = err.response?.data;
    const error = new Error(data?.message || err.message);
    error.statusCode = err.response?.status || err.status;
    error.code = err.code;
    error.redirectable = data?.redirectable;
    error.isBackendError = true;
    error.source = source;
    error.type = _getErrorType(err);
    throw error;
  }
};

export default {
  fetchPreAuthnConfig: async (data) => {
    return fetchWithTimeout({
      authnEndpoint: preAuthnEndpoint,
      options: generateOptions(data),
      source: 'pre-authn-spa',
    });
  },
  fetchPostAuthnConfig: async (data, accessToken, onRetry = () => {}) => {
    const options = generateOptions(data);
    options.headers.Authorization = `Bearer ${accessToken}`;
    return fetchWithTimeout({
      authnEndpoint: postAuthnEndpoint,
      options,
      onRetry,
      source: 'post-authn',
    });
  },
  fetchManageProfileConfig: async (data, onRetry = () => {}) => {
    return fetchWithTimeout({
      authnEndpoint: manageProfileEndpoint,
      options: generateOptions(data),
      onRetry,
    });
  },
  processCredsApi: async (data) => {
    return fetchWithTimeout({
      authnEndpoint: processCredsEndpoint,
      options: generateOptions(data),
      source: 'process-creds',
    });
  },
};
